<script setup>
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import LibraryCard from './LibraryCard.vue'
import RemoveResourceButton from './RemoveResourceButton.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { getPlAreaLabelsFromValues } from '@/contentprofile/no_sel_composable'
import DynamicHeading from '@/components/DynamicHeading.vue'
import { getPresentLevelsUrl } from '@/util/links'
import CardContributor from './CardContributor.vue'
import { useContributorVisibility } from './composables/useContributorVisibility'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/tooltip'
import { useVisibilityStore } from '@/visibility/visibility.store'


const props = defineProps({
  presentLevel: {
    type: Object,
    required: true
  },
  removable: {
    type: Boolean,
    default: true
  },
  shared: {
    type: Boolean,
    default: false
  },
  clickable: {
    type: Boolean,
    default: true
  },
  headingLevel: {
    type: Number,
    default: 3
  }
})

const { contributorIsVisible } = useContributorVisibility(props.presentLevel.content_uuid)
const visibilityStore = useVisibilityStore()
const { effectsVisibility, aiFeatureVisibility } = storeToRefs(visibilityStore)

const resourceType = ref(CONTENT_TYPES.PRESENTLEVEL)

// Only show read-only styling when effectsVisibility is false AND aiFeatureVisibility is true
const isReadOnly = computed(() => effectsVisibility.value === false && aiFeatureVisibility.value === true)

const presentLevelsUrl = computed(() => {
  const url = getPresentLevelsUrl({
    id: props.presentLevel.content_uuid,
    isShared: props.shared
  })
  return url
})
const plHeading = computed(() => {
  return (
    props.presentLevel.content_body.student_name +
    ' (' +
    props.presentLevel.content_body.enrolled_grade_level +
    ' grade)'
  )
})
const plAreasStatement = computed(() => {
  return 'Present level areas: '.concat(
    getPlAreaLabelsFromValues(props.presentLevel.content_body.selected_subjects).join(', ')
  )
})
</script>
<template>
  <div
    class="relative [&_.remove-resource-button]:hover:opacity-100 [&_.remove-resource-button]:focus:opacity-100 [&_.card-title]:hover:text-gray-1000 [&_.card-title]:focus:text-gray-1000"
  >
    <LibraryCard
      :content-uuid="presentLevel.content_uuid"
      :show-folder-name="presentLevel.show_folder_name"
      :folder-name="presentLevel.collection_body?.name"
      :url="presentLevelsUrl"
      :updated-time="presentLevel.content_body.updated_at"
      v-bind:clickable="clickable"
      :bg-color="isReadOnly ? 'bg-gray-50' : 'bg-surface-50'"
      :hide-footer-band="isReadOnly"
    >
      <div class="flex items-start gap-2">
        <TooltipProvider v-if="isReadOnly" :delay-duration="0">
          <Tooltip>
            <TooltipTrigger as-child>
              <span 
                class="material-icons text-weak text-lg flex-shrink-0 cursor-help leading-none" 
                tabindex="0" 
                role="button"
                aria-label="Read-only content information">lock</span>
            </TooltipTrigger>
            <TooltipContent class="max-w-xs">
              <p><span class="material-icons text-sm align-middle mr-1">lock</span>This content is now <strong>read-only and cannot be edited</strong>, since it was created in a previous version of the Present Levels Wizard.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DynamicHeading
          :headingLevel="props.headingLevel"
          :class="`header-sm ${contributorIsVisible ? 'pb-0' : 'pb-3'} text-xl ${isReadOnly ? 'text-weak leading-none' : 'text-primary-500'} card-title fs-exclude flex-1`"
          data-cy="present-levels-title"
        >
          {{ plHeading }}
        </DynamicHeading>
      </div>
      <CardContributor
        v-if="!shared"
        :contentId="presentLevel.content_uuid"
      />
      <p
        :class="`min-h-11 overflow-hidden text-ellipsis text-md ${isReadOnly ? 'text-weak' : ''} [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]`"
      >
        {{ plAreasStatement }}
      </p>
      <template #footer-right>
        <span :class="`flex items-center text-sm font-bold ${isReadOnly ? 'text-weak' : ''}`">
          <span :class="`material-icons-outlined text-lg ${isReadOnly ? 'text-weak' : ''}`">file_download</span>
          {{ presentLevel.content_body.resource_count || 0 }}
        </span>
      </template>
    </LibraryCard>
    <RemoveResourceButton
      v-if="!shared && removable"
      :resource="presentLevel.content_uuid"
      :collection="presentLevel.collection_uuid"
      :type="resourceType"
      :ariaLabel="`Remove statement from collection`"
    />
  </div>
</template>
